import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(3);
  const [token, setToken] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setStatus('error');
      setMessage('Invalid reset link. No token provided.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!newPassword || !confirmPassword) {
      setStatus('error');
      setMessage('Please fill in all fields');
      return;
    }

    if (newPassword.length < 5) {
      setStatus('error');
      setMessage('Password must be at least 5 characters long');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setStatus('error');
      setMessage('Password must contain at least one capital letter');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setStatus('error');
      setMessage('Password must contain at least one number');
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match');
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: newPassword,
          confirmPassword: confirmPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message || 'Password reset successfully! You can now login.');

        // SECURITY: Clear all auth tokens (logs out all tabs/windows)
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('loginMode');
        localStorage.removeItem('lastActivity');

        // Broadcast logout to all other tabs/windows
        localStorage.setItem('logout-event', Date.now().toString());
      } else {
        setStatus('error');
        setMessage(data.error || 'Password reset failed. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
      console.error('Reset password error:', error);
    }
  };

  // Countdown and redirect on success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/?mode=doctor');
    }
  }, [status, countdown, navigate]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {!token && status === 'error' ? (
          <div style={styles.content}>
            <div style={styles.errorIcon}>✕</div>
            <h2 style={styles.title}>Invalid Reset Link</h2>
            <p style={styles.text}>{message}</p>
            <button onClick={() => navigate('/?mode=doctor')} style={styles.button}>
              Go to Login
            </button>
          </div>
        ) : status === 'success' ? (
          <div style={styles.content}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.title}>Password Reset Successfully!</h2>
            <p style={styles.text}>{message}</p>
            <p style={styles.countdown}>
              Redirecting to login in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
            <button onClick={() => navigate('/?mode=doctor')} style={styles.button}>
              Go to Login Now
            </button>
          </div>
        ) : (
          <div style={styles.content}>
            <h2 style={styles.title}>Create New Password</h2>
            <p style={styles.subtitle}>
              Please enter your new password below.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputGroup}>
                <label htmlFor="newPassword" style={styles.label}>
                  New Password
                </label>
                <div style={styles.passwordInputWrapper}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    style={styles.input}
                    disabled={status === 'submitting'}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={styles.eyeButton}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label htmlFor="confirmPassword" style={styles.label}>
                  Confirm Password
                </label>
                <div style={styles.passwordInputWrapper}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    style={styles.input}
                    disabled={status === 'submitting'}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={styles.eyeButton}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.passwordHint}>
                <p style={styles.hintText}>Password requirements:</p>
                <ul style={styles.hintList}>
                  <li style={newPassword.length >= 5 ? styles.hintValid : {}}>
                    {newPassword.length >= 5 ? '✓' : '○'} At least 5 characters
                  </li>
                  <li style={/[A-Z]/.test(newPassword) ? styles.hintValid : {}}>
                    {/[A-Z]/.test(newPassword) ? '✓' : '○'} At least one capital letter
                  </li>
                  <li style={/[0-9]/.test(newPassword) ? styles.hintValid : {}}>
                    {/[0-9]/.test(newPassword) ? '✓' : '○'} At least one number
                  </li>
                  <li
                    style={
                      newPassword && confirmPassword && newPassword === confirmPassword
                        ? styles.hintValid
                        : {}
                    }
                  >
                    {newPassword && confirmPassword && newPassword === confirmPassword ? '✓' : '○'} Passwords match
                  </li>
                </ul>
              </div>

              {status === 'error' && message && (
                <div style={styles.errorBox}>
                  <p style={styles.errorText}>{message}</p>
                </div>
              )}

              <button
                type="submit"
                style={{
                  ...styles.submitButton,
                  ...(status === 'submitting' ? styles.submitButtonDisabled : {}),
                }}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Resetting Password...' : 'Reset Password'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/?mode=doctor')}
                style={styles.cancelButton}
              >
                Back to Login
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `
      radial-gradient(circle at 18% 22%, rgba(34, 211, 238, 0.16), transparent 30%),
      radial-gradient(circle at 80% 8%, rgba(99, 102, 241, 0.2), transparent 32%),
      linear-gradient(135deg, #0b1224, #0c1e38)
    `,
    padding: '20px',
  },
  card: {
    background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.12), rgba(99, 102, 241, 0.16))',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
  },
  content: {
    textAlign: 'center',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '12px',
    marginTop: '20px',
  },
  subtitle: {
    fontSize: '15px',
    color: '#e2e8f0',
    marginBottom: '30px',
    lineHeight: '1.6',
  },
  text: {
    fontSize: '16px',
    color: '#e2e8f0',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '8px',
  },
  passwordInputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    paddingRight: '48px',
    fontSize: '15px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: '#ffffff',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.3s',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#e2e8f0',
    transition: 'color 0.2s',
    outline: 'none',
  },
  passwordHint: {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderLeft: '4px solid #14b8a6',
    padding: '12px 16px',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  hintText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '8px',
  },
  hintList: {
    fontSize: '13px',
    color: '#e2e8f0',
    paddingLeft: '20px',
    margin: '0',
    listStyleType: 'none',
  },
  hintValid: {
    color: '#14b8a6',
    fontWeight: '600',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #0f9b8e 0%, #14b8a6 50%, #a7f3d0 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 30px rgba(20, 184, 166, 0.35)',
    marginBottom: '12px',
  },
  submitButtonDisabled: {
    background: 'linear-gradient(135deg, #6b8886 0%, #7a9d98 50%, #c7ddd6 100%)',
    cursor: 'not-allowed',
    boxShadow: '0 5px 15px rgba(20, 184, 166, 0.15)',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#14b8a6',
    border: '2px solid rgba(20, 184, 166, 0.5)',
    borderRadius: '14px',
    padding: '14px 32px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  button: {
    background: 'linear-gradient(135deg, #0f9b8e 0%, #14b8a6 50%, #a7f3d0 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 30px rgba(20, 184, 166, 0.35)',
    marginTop: '10px',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
    backgroundColor: '#10b981',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: 'white',
    fontWeight: 'bold',
  },
  errorIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
    backgroundColor: '#ef4444',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: 'white',
    fontWeight: 'bold',
  },
  countdown: {
    fontSize: '14px',
    color: '#cbd5e1',
    marginBottom: '20px',
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    borderLeft: '4px solid #ef4444',
    padding: '16px',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  errorText: {
    fontSize: '14px',
    color: '#991b1b',
    margin: '0',
    fontWeight: '500',
  },
};

export default ResetPassword;
